import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Pencil, CalendarX, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { toast } from "sonner";
import CascadeDeleteDialog from "@/components/CascadeDeleteDialog";
import PushOptInCard from "@/components/PushOptInCard";

export default function InstellingenPage() {
  const { user } = useAuth();
  return (
    <div>
      <PageHeader
        title="Instellingen"
        subtitle="Organisatie-configuratie: locaties, groepen en gebruikers."
        testid="instellingen-header"
      />
      <Tabs defaultValue="locations" className="w-full">
        <div className="w-full overflow-x-auto -mx-1 px-1">
          <TabsList className="bg-white border border-neutral-200 inline-flex w-max whitespace-nowrap">
            <TabsTrigger value="locations" data-testid="tab-locations">Locaties</TabsTrigger>
            <TabsTrigger value="groups" data-testid="tab-groups">Groepen</TabsTrigger>
            <TabsTrigger value="meldingen" data-testid="tab-meldingen">Meldingen</TabsTrigger>
            {can(user, "users", "read") && <TabsTrigger value="users" data-testid="tab-users">Gebruikers</TabsTrigger>}
            {user?.role === "superadmin" && <TabsTrigger value="digest" data-testid="tab-digest">Weekdigest</TabsTrigger>}
            {user?.role === "superadmin" && <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll-koppeling</TabsTrigger>}
            {(user?.role === "superadmin" || user?.role === "locatiemanager") && <TabsTrigger value="sluitdagen" data-testid="tab-sluitdagen">Sluitdagen</TabsTrigger>}
            {(user?.role === "superadmin" || user?.role === "directeur" || user?.role === "locatiemanager") && <TabsTrigger value="sluitingsperiodes" data-testid="tab-sluitingsperiodes">Sluitingsperiodes</TabsTrigger>}
            {user?.role === "superadmin" && <TabsTrigger value="import" data-testid="tab-import">Data-import</TabsTrigger>}
            {(user?.role === "superadmin" || user?.role === "directeur" || user?.role === "platform_admin") && <TabsTrigger value="cleanup" data-testid="tab-cleanup">Demo-data opschonen</TabsTrigger>}
          </TabsList>
        </div>
        <TabsContent value="locations" className="mt-6"><Locations /></TabsContent>
        <TabsContent value="groups" className="mt-6"><Groups /></TabsContent>
        <TabsContent value="meldingen" className="mt-6"><MeldingenTab /></TabsContent>
        {can(user, "users", "read") && <TabsContent value="users" className="mt-6"><Users /></TabsContent>}
        {user?.role === "superadmin" && <TabsContent value="digest" className="mt-6"><DigestTab /></TabsContent>}
        {user?.role === "superadmin" && <TabsContent value="payroll" className="mt-6"><PayrollTab /></TabsContent>}
        {(user?.role === "superadmin" || user?.role === "locatiemanager") && <TabsContent value="sluitdagen" className="mt-6"><SluitdagenTab /></TabsContent>}
        {(user?.role === "superadmin" || user?.role === "directeur" || user?.role === "locatiemanager") && <TabsContent value="sluitingsperiodes" className="mt-6"><SluitingsperiodesTab /></TabsContent>}
        {user?.role === "superadmin" && <TabsContent value="import" className="mt-6"><DataImportTab /></TabsContent>}
        {(user?.role === "superadmin" || user?.role === "directeur" || user?.role === "platform_admin") && <TabsContent value="cleanup" className="mt-6"><CleanupTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

function MeldingenTab() {
  return (
    <div className="max-w-2xl">
      <div className="text-sm text-neutral-600 mb-4">
        Beheer pushmeldingen op dit apparaat. Meldingen worden ook via de bel-icoon in de app getoond.
        Op iOS moet de app eerst op je beginscherm staan voordat meldingen werken.
      </div>
      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        <PushOptInCard />
      </div>
    </div>
  );
}

function Locations() {
  const { user } = useAuth();
  const canWrite = can(user, "locations", "write");
  const canDelete = can(user, "locations", "delete");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "KDV", lrk_number: "", address: "", postcode: "", city: "", phone: "" });
  const [deleteFor, setDeleteFor] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(() => api.get("/locations").then(r => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const moveRow = async (idx, direction) => {
    // Reorder lokaal + persist via POST /locations/reorder
    const newRows = [...rows];
    const target = idx + direction;
    if (target < 0 || target >= newRows.length) return;
    [newRows[idx], newRows[target]] = [newRows[target], newRows[idx]];
    setRows(newRows);  // optimistic
    try {
      await api.post("/locations/reorder", { ids: newRows.map(r => r.id) });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail || "Verplaatsen mislukt"));
      load();  // rollback
    }
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || "", type: r.type || "KDV", lrk_number: r.lrk_number || "",
      address: r.address || "", postcode: r.postcode || "", city: r.city || "", phone: r.phone || "",
    });
    setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditingId(null); setForm({ name: "", type: "KDV", lrk_number: "", address: "", postcode: "", city: "", phone: "" }); };
  const submit = async () => {
    try {
      if (editingId) {
        await api.put(`/locations/${editingId}`, form);
        toast.success("Locatie bijgewerkt");
      } else {
        await api.post("/locations", form);
        toast.success("Locatie aangemaakt");
      }
      closeDialog(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const columns = [
    { key: "name", label: "Naam", render: r => <div className="font-medium">{r.name}</div> },
    { key: "type", label: "Type", render: r => <Badge variant="outline">{r.type}</Badge> },
    { key: "lrk_number", label: "LRK-nummer", render: r => <span className="font-mono text-xs">{r.lrk_number || "—"}</span> },
    { key: "address", label: "Adres", render: r => <div><div>{r.address}</div><div className="text-xs text-neutral-500">{r.postcode} {r.city}</div></div> },
    { key: "phone", label: "Telefoon" },
    ...(canWrite || canDelete ? [{ key: "_act", label: "", sortable: false, render: r => {
      const idx = rows.findIndex(x => x.id === r.id);
      return (
      <div className="flex items-center gap-1 justify-end">
        {canWrite && (
          <>
            <Button size="sm" variant="ghost" data-testid={`move-up-location-${r.id}`}
              disabled={idx <= 0} title="Omhoog"
              onClick={(e) => { e.stopPropagation(); moveRow(idx, -1); }} className="h-8">
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" data-testid={`move-down-location-${r.id}`}
              disabled={idx >= rows.length - 1} title="Omlaag"
              onClick={(e) => { e.stopPropagation(); moveRow(idx, 1); }} className="h-8">
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" data-testid={`edit-location-${r.id}`}
              onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="h-8">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
        {canDelete && (
          <Button size="sm" variant="ghost" data-testid={`delete-location-${r.id}`}
            onClick={(e) => { e.stopPropagation(); setDeleteFor(r); }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      );
    }}] : []),
  ];

  return (
    <div>
      {canWrite && (
        <div className="mb-4 flex justify-end">
          <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); else setOpen(true); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-location-btn" style={{ background: "var(--sage)" }} className="text-white"><Plus className="h-4 w-4 mr-1" /> Nieuwe locatie</Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle>{editingId ? "Locatie wijzigen" : "Nieuwe locatie"}</DialogTitle><DialogDescription>{editingId ? "Pas de gegevens van deze locatie aan." : "Voeg een KDV, BSO of peuteropvang toe."}</DialogDescription></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Naam</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="location-name-input" />
                  {(() => {
                    const n = (form.name || "").trim();
                    const nLow = n.toLowerCase();
                    const hasMarker = ["kdv", "bso", "psz", "loc", "vestiging", "hoofd"].some(m => nLow.includes(m));
                    // 1) Persoonsnaam-formaat
                    if (n.includes(",") && !hasMarker) {
                      return (
                        <div className="mt-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="location-name-warn">
                          ⚠ Naam heeft persoonsnaam-formaat (komma). Voeg "KDV", "BSO" of "Locatie" toe om te bevestigen dat dit een KDV is.
                        </div>
                      );
                    }
                    // 2) Duplicaat-check (case-insensitive) — negeert de eigen locatie bij edit
                    if (n) {
                      const dup = rows.find(r => (r.name || "").trim().toLowerCase() === nLow && r.id !== editingId);
                      if (dup) {
                        return (
                          <div className="mt-1 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="location-name-dup">
                            ⚠ Er bestaat al een locatie met de naam <strong>"{dup.name}"</strong>. Kies een unieke naam (hoofdletters worden genegeerd).
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}
                </div>
                <div><Label>Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="KDV">KDV</SelectItem>
                      <SelectItem value="BSO">BSO</SelectItem>
                      <SelectItem value="PSZ">Peuteropvang</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>LRK-nummer</Label><Input value={form.lrk_number} onChange={e => setForm({ ...form, lrk_number: e.target.value })} /></div>
                <div className="col-span-2"><Label>Adres</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
                <div><Label>Postcode</Label><Input value={form.postcode} onChange={e => setForm({ ...form, postcode: e.target.value })} /></div>
                <div><Label>Stad</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
                <div className="col-span-2"><Label>Telefoon</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={closeDialog}>Annuleren</Button>
                <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white" data-testid="location-save">{editingId ? "Wijzigingen opslaan" : "Opslaan"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      <DataTable columns={columns} rows={rows} testid="locations-table" />
      <CascadeDeleteDialog
        open={!!deleteFor}
        onClose={() => setDeleteFor(null)}
        resource="locations"
        resourceId={deleteFor?.id}
        resourceLabel={deleteFor?.name}
        onDeleted={load}
      />
    </div>
  );
}

function Groups() {
  const { user } = useAuth();
  const canWrite = can(user, "groups", "write");
  const canDelete = can(user, "groups", "delete");
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", location_id: "", capacity: 16, min_age_months: 0, max_age_months: 48, color: "#2D4A3E", allergenen_aanwezig: [] });
  const [deleteFor, setDeleteFor] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    const [g, l] = await Promise.all([api.get("/groups"), api.get("/locations")]);
    setRows(g.data); setLocations(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const moveRow = async (idx, direction) => {
    const newRows = [...rows];
    const target = idx + direction;
    if (target < 0 || target >= newRows.length) return;
    [newRows[idx], newRows[target]] = [newRows[target], newRows[idx]];
    setRows(newRows);
    try {
      await api.post("/groups/reorder", { ids: newRows.map(r => r.id) });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail || "Verplaatsen mislukt"));
      load();
    }
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || "", location_id: r.location_id || "",
      capacity: r.capacity ?? 16,
      min_age_months: r.min_age_months ?? 0,
      max_age_months: r.max_age_months ?? 48,
      color: r.color || "#2D4A3E",
      allergenen_aanwezig: r.allergenen_aanwezig || [],
    });
    setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditingId(null); setForm({ name: "", location_id: "", capacity: 16, min_age_months: 0, max_age_months: 48, color: "#2D4A3E", allergenen_aanwezig: [] }); };
  const submit = async () => {
    try {
      const payload = { ...form, capacity: parseInt(form.capacity), min_age_months: parseInt(form.min_age_months), max_age_months: parseInt(form.max_age_months), allergenen_aanwezig: form.allergenen_aanwezig || [] };
      if (editingId) {
        await api.put(`/groups/${editingId}`, payload);
        toast.success("Groep bijgewerkt");
      } else {
        await api.post("/groups", payload);
        toast.success("Groep aangemaakt");
      }
      closeDialog(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const locMap = Object.fromEntries(locations.map(l => [l.id, l.name]));
  const columns = [
    { key: "name", label: "Naam", render: r => <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ background: r.color }} /><span className="font-medium">{r.name}</span></div> },
    { key: "location_id", label: "Locatie", accessor: r => locMap[r.location_id] },
    { key: "capacity", label: "Capaciteit" },
    { key: "min_age_months", label: "Leeftijd", render: r => <span className="text-xs">{Math.floor(r.min_age_months / 12)}-{Math.floor(r.max_age_months / 12)} jaar</span> },
    ...(canWrite || canDelete ? [{ key: "_act", label: "", sortable: false, render: r => {
      const idx = rows.findIndex(x => x.id === r.id);
      return (
      <div className="flex items-center gap-1 justify-end">
        {canWrite && (
          <>
            <Button size="sm" variant="ghost" data-testid={`move-up-group-${r.id}`}
              disabled={idx <= 0} title="Omhoog"
              onClick={(e) => { e.stopPropagation(); moveRow(idx, -1); }} className="h-8">
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" data-testid={`move-down-group-${r.id}`}
              disabled={idx >= rows.length - 1} title="Omlaag"
              onClick={(e) => { e.stopPropagation(); moveRow(idx, 1); }} className="h-8">
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" data-testid={`edit-group-${r.id}`}
              onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="h-8">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
        {canDelete && (
          <Button size="sm" variant="ghost" data-testid={`delete-group-${r.id}`}
            onClick={(e) => { e.stopPropagation(); setDeleteFor(r); }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      );
    }}] : []),
  ];

  return (
    <div>
      {canWrite && (
        <div className="mb-4 flex justify-end">
          <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); else setOpen(true); }}>
            <DialogTrigger asChild>
              <Button style={{ background: "var(--sage)" }} className="text-white" data-testid="add-group-btn"><Plus className="h-4 w-4 mr-1" /> Nieuwe groep</Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle>{editingId ? "Groep wijzigen" : "Nieuwe groep"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Naam</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>Locatie</Label>
                  <Select value={form.location_id} onValueChange={v => setForm({ ...form, location_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Kies locatie" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Capaciteit</Label><Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} /></div>
                <div><Label>Kleur</Label><Input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} /></div>
                <div><Label>Min leeftijd (mnd)</Label><Input type="number" value={form.min_age_months} onChange={e => setForm({ ...form, min_age_months: e.target.value })} /></div>
                <div><Label>Max leeftijd (mnd)</Label><Input type="number" value={form.max_age_months} onChange={e => setForm({ ...form, max_age_months: e.target.value })} /></div>
              </div>
              {/* WEBSTER KOV — Allergie-Groep Config: expliciete allergenen in deze groep aanwezig.
                  Wordt gebruikt door de drag-drop allergie-check in het rooster. */}
              <div className="mt-3 border-t border-neutral-200 pt-3">
                <Label className="text-xs uppercase tracking-widest text-neutral-600">Allergenen aanwezig in deze groep</Label>
                <p className="text-[10px] text-neutral-500 mb-2">Vink aan welke allergenen circuleren. De rooster-planner waarschuwt dan bij verplaatsen van een kind met deze allergie.</p>
                <div className="flex flex-wrap gap-2">
                  {["pinda", "noten", "melk", "ei", "gluten", "soja", "vis", "sesam", "schaaldieren", "vega"].map(all => {
                    const checked = (form.allergenen_aanwezig || []).includes(all);
                    return (
                      <label key={all}
                             className={`text-[11px] px-2 py-1 rounded border cursor-pointer transition-colors ${checked ? "bg-red-50 border-red-300 text-red-800" : "bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50"}`}
                             data-testid={`group-allergie-${all}`}>
                        <input type="checkbox" checked={checked}
                               onChange={(e) => {
                                 const next = new Set(form.allergenen_aanwezig || []);
                                 if (e.target.checked) next.add(all);
                                 else next.delete(all);
                                 setForm({ ...form, allergenen_aanwezig: Array.from(next) });
                               }}
                               className="mr-1 align-middle" />
                        {all}
                      </label>
                    );
                  })}
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={closeDialog}>Annuleren</Button>
                <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white" data-testid="group-save">{editingId ? "Wijzigingen opslaan" : "Opslaan"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      <DataTable columns={columns} rows={rows} testid="groups-table" />
      <CascadeDeleteDialog
        open={!!deleteFor}
        onClose={() => setDeleteFor(null)}
        resource="groups"
        resourceId={deleteFor?.id}
        resourceLabel={deleteFor?.name}
        onDeleted={load}
      />
    </div>
  );
}

const ROLE_LABEL = { superadmin: "Superadmin", locatiemanager: "Locatiemanager", financieel: "Financieel", pedagogisch: "Pedagogisch" };

function Users() {
  const { user } = useAuth();
  const canWrite = can(user, "users", "write");
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "pedagogisch", password: "", location_ids: [], group_ids: [] });

  const load = useCallback(async () => {
    const [u, l] = await Promise.all([api.get("/users"), api.get("/locations")]);
    setRows(u.data); setLocations(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/users", form); toast.success("Gebruiker aangemaakt"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resend = async (uid) => {
    try {
      const r = await api.post(`/users/${uid}/invite`);
      toast.success(r.data.invitation?.sent ? "Uitnodiging opnieuw verzonden" : `Verstuurd (${r.data.invitation?.reason || "geen mail-provider"})`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const locMap = Object.fromEntries(locations.map(l => [l.id, l.name]));

  const statusBadge = (r) => {
    if (r.status === "uitnodiging_open" || (!r.active && r.invited_at)) {
      return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 border">Uitnodiging open</Badge>;
    }
    if (r.active) return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 border">Actief</Badge>;
    return <Badge variant="outline" className="bg-neutral-100 text-neutral-500 border-neutral-200 border">Inactief</Badge>;
  };

  const columns = [
    { key: "name", label: "Naam", render: r => (
      <div>
        <div className="font-medium">{r.name}</div>
        <div className="text-xs text-neutral-500">{r.email}</div>
      </div>
    )},
    { key: "role", label: "Rol", render: r => (
      <Badge variant="outline" className="bg-[color:var(--sage-100)] text-[color:var(--sage)] border-transparent">{ROLE_LABEL[r.role]}</Badge>
    )},
    { key: "location_ids", label: "Locatiescope", sortable: false, render: r => r.location_ids?.length
      ? <div className="flex flex-wrap gap-1">{r.location_ids.map(id => <Badge key={id} variant="outline" className="text-xs">{locMap[id]}</Badge>)}</div>
      : <span className="text-xs text-neutral-400">Alle locaties</span> },
    { key: "status", label: "Status", render: statusBadge },
    { key: "actions", label: "", sortable: false, render: r => (
      (r.status === "uitnodiging_open" || (!r.active && r.invited_at)) && canWrite ? (
        <Button size="sm" variant="outline" onClick={() => resend(r.id)} data-testid={`resend-invite-${r.id}`} className="h-7 text-xs">
          Opnieuw versturen
        </Button>
      ) : null
    )},
  ];

  return (
    <div>
      {canWrite && (
        <div className="mb-4 flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button style={{ background: "var(--sage)" }} className="text-white" data-testid="add-user-btn"><Plus className="h-4 w-4 mr-1" /> Nieuwe gebruiker</Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle>Nieuwe gebruiker</DialogTitle><DialogDescription>Er wordt een activatie-uitnodiging (72u geldig) verstuurd naar het opgegeven e-mailadres. De gebruiker stelt zelf een wachtwoord in.</DialogDescription></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Naam</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div>
                  <Label>Rol</Label>
                  <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {Object.entries(ROLE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Wachtwoord</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Optioneel — user activeert zelf" /></div>
                <div className="col-span-2">
                  <Label>Locatiescope</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {locations.map(l => {
                      const on = form.location_ids.includes(l.id);
                      return (
                        <button key={l.id} type="button"
                          onClick={() => setForm({ ...form, location_ids: on ? form.location_ids.filter(x => x !== l.id) : [...form.location_ids, l.id] })}
                          className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${on ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]" : "border-neutral-200 hover:bg-neutral-100"}`}
                        >{l.name}</button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white">Opslaan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      <DataTable columns={columns} rows={rows} testid="users-table" />
    </div>
  );
}

function DigestTab() {
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    setLoading(true); setResult(null);
    try {
      const url = to ? `/digest/test?to=${encodeURIComponent(to)}` : "/digest/test";
      const r = await api.post(url);
      setResult(r.data);
      toast.success(r.data.sent ? `Verzonden naar ${r.data.to}` : "Niets te versturen deze week");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="max-w-xl">
      <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="digest-panel">
        <h3 className="font-display text-xl tracking-tight mb-2">Wekelijkse digest</h3>
        <p className="text-sm text-neutral-600 mb-4">
          Automatisch elke maandag om 08:00 Europe/Amsterdam (lokale tijd, wisselt automatisch mee met zomer-/wintertijd)
          naar alle ouders met e-mailadres. Test hieronder handmatig — laat leeg om naar de eerste ouder te sturen,
          of vul een adres in om alleen daar te testen.
        </p>
        <div className="flex items-center gap-2">
          <Input type="email" placeholder="test@voorbeeld.nl (optioneel)" value={to} onChange={e => setTo(e.target.value)}
            data-testid="digest-to" className="flex-1" />
          <Button onClick={send} disabled={loading} data-testid="digest-send"
            style={{ background: "var(--sage)" }} className="text-white">
            {loading ? "Versturen…" : "Digest versturen"}
          </Button>
        </div>
        {result && (
          <div className="mt-4 p-3 rounded-md bg-neutral-50 border border-neutral-200 text-sm" data-testid="digest-result">
            <div className="font-medium">{result.sent ? "✓ Verzonden" : "Niets te versturen"}</div>
            {result.to && <div className="text-neutral-600">Naar: <span className="font-mono">{result.to}</span></div>}
            {result.subject && <div className="text-neutral-600">Onderwerp: {result.subject}</div>}
          </div>
        )}
      </div>
      <div className="mt-4 text-xs text-neutral-500">
        Cron-schedule staat in <code className="bg-neutral-100 px-1 rounded">.emergent/crons.yml</code>. Endpoint /api/cron/digest is beveiligd met bearer secret.
      </div>
    </div>
  );
}

/** Payroll-integratie tab — laat de API-key en endpoints zien voor de Nanny-Payroll app. */
function PayrollTab() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get("/payroll/admin/config")
      .then(r => setCfg(r.data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);
  if (!cfg) return <div className="text-sm text-neutral-500">Laden…</div>;

  const backend = process.env.REACT_APP_BACKEND_URL;
  const example = `curl -H "X-API-Key: <YOUR_KEY>" \\
  ${backend}/api/payroll/medewerkers`;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="payroll-tab">
      <div>
        <h2 className="font-display text-xl mb-1">Payroll-koppeling (Nanny-Payroll)</h2>
        <p className="text-sm text-neutral-600">
          De Nanny-Payroll app haalt medewerkers, uren en verlof op via deze REST-API met een
          <strong> X-API-Key</strong> header. Webhooks sturen automatisch pushes bij mutaties.
        </p>
      </div>

      <div className="p-4 rounded-md border border-neutral-200 bg-white" data-testid="payroll-key">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">API-key status</div>
        {cfg.configured ? (
          <div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Actief</Badge>
              <code className="text-sm bg-neutral-100 px-2 py-0.5 rounded">{cfg.key_masked}</code>
            </div>
            <p className="text-[11px] text-neutral-500 mt-2">
              De volledige key staat in <code className="bg-neutral-100 px-1 rounded">/app/backend/.env</code> onder
              <code className="bg-neutral-100 px-1 rounded ml-1">PAYROLL_API_KEY</code>. Roteer met
              <code className="bg-neutral-100 px-1 rounded ml-1">python3 -c "import secrets;print('np_'+secrets.token_urlsafe(32))"</code>
              en herstart de backend.
            </p>
          </div>
        ) : (
          <Badge className="bg-red-50 text-red-700 border border-red-200">Niet geconfigureerd — voeg PAYROLL_API_KEY toe aan .env</Badge>
        )}
      </div>

      <div>
        <h3 className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Endpoints</h3>
        <div className="rounded-md border border-neutral-200 bg-white divide-y divide-neutral-100">
          {Object.entries(cfg.endpoints).map(([k, v]) => (
            <div key={k} className="p-2.5 flex items-center justify-between gap-3">
              <div className="text-sm font-medium text-neutral-800">{k}</div>
              <code className="text-xs text-neutral-600 bg-neutral-50 px-2 py-1 rounded">{v}</code>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Webhook events</h3>
        <div className="flex flex-wrap gap-2">
          {cfg.events.map(ev => (
            <Badge key={ev} variant="outline" className="text-xs">{ev}</Badge>
          ))}
        </div>
        <p className="text-[11px] text-neutral-500 mt-2">
          Registreer een webhook-URL op de "Koppelingen"-pagina en abonneer op deze events om automatische
          pushes te ontvangen. HMAC-signature via X-Webster-Signature header.
        </p>
      </div>

      <div>
        <h3 className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Voorbeeld</h3>
        <pre className="p-3 rounded-md bg-neutral-900 text-neutral-100 text-xs overflow-x-auto">{example}</pre>
      </div>
    </div>
  );
}

/** Data-import tab — éénmalige synchronisatie van Webster .xls/.csv → productie DB.
 * Zichtbaar voor superadmin. Roept POST /api/admin/import-nanny-data aan met de safety-token.
 * Bevat een drag-and-drop upload UI om nieuwe bronbestanden te uploaden zonder shell-toegang. */
function DataImportTab() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [forceMode, setForceMode] = useState(false);
  const [sourceFiles, setSourceFiles] = useState([]);
  const [filesError, setFilesError] = useState(null);

  const loadSourceFiles = useCallback(async () => {
    setFilesError(null);
    try {
      const { data } = await api.get("/admin/import-nanny-data/files");
      setSourceFiles(data.files || []);
    } catch (e) {
      setFilesError(
        formatApiError(e.response?.data?.detail) ||
          "Kon bronbestand-status niet ophalen. Controleer je rechten of probeer opnieuw."
      );
      setSourceFiles([]);
    }
  }, []);
  useEffect(() => { loadSourceFiles(); }, [loadSourceFiles]);

  const runImport = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/import-nanny-data", {
        confirm: "IMPORT-NANNY-2026",
        force: forceMode,
      });
      // Repareer medewerker-referenties direct na een succesvolle import,
      // anders wijzen bestaande medewerkers.location_ids/group_ids naar
      // verwijderde demo-UUIDs en kan geen enkele shift worden opgeslagen.
      let repair = null;
      let repairErr = null;
      try {
        const rr = await api.post("/admin/repair-medewerker-references");
        repair = rr.data;
      } catch (e) {
        repairErr = formatApiError(e.response?.data?.detail) || "Reparatie faalde";
      }
      setResult({ ...data, repair, repair_error: repairErr });
      if (repairErr) {
        toast.warning(
          `Import gelukt (${data.kinderen_totaal} kinderen), maar reparatie van medewerker-referenties faalde: ${repairErr}. Gebruik de "Repareer nu" knop hieronder.`
        );
      } else {
        toast.success(
          `Import gelukt · ${data.kinderen_totaal} kinderen · ${data.ouders_totaal} ouders` +
          (repair ? ` · ${repair.repaired} medewerker-refs hersteld` : "")
        );
      }
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Import mislukt";
      toast.error(msg);
      setResult({ error: msg });
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="data-import-tab">
      <div>
        <h2 className="font-display text-xl mb-1">Data-import (Webster → Childcare Ops)</h2>
        <p className="text-sm text-neutral-600">
          Synchroniseert de kinder- en ouderdata uit de eerder geüploade
          <code className="bg-neutral-100 px-1 rounded mx-1">.xls</code>/
          <code className="bg-neutral-100 px-1 rounded mx-1">.csv</code>
          bestanden naar de huidige database. Verwijdert eerst demo-locaties
          (Zonnebloem/Klimboom) en maakt de 3 echte locaties:
          <strong> Middenweg Babygroep, Middenweg Dreumes en Peuters, Hoofddorpplein</strong>.
        </p>
      </div>

      <div className="p-4 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-900">
        <div className="font-semibold mb-1">Let op — destructieve operatie</div>
        <ul className="list-disc ml-5 space-y-0.5 text-[13px]">
          <li>Alle demo-data (Zonnebloem/Klimboom) wordt permanent verwijderd.</li>
          <li>
            <strong>Force-modus</strong> verwijdert óók de 3 echte locaties + gekoppelde kinderen,
            ouders, contracten en presentie voordat het opnieuw importeert. Gebruik dit alleen om
            een fout uit een eerdere import te corrigeren.
          </li>
          <li>Zonder force draait de import alleen als de 3 locaties nog niet bestaan.</li>
        </ul>
      </div>

      <ImportSourceUploader files={sourceFiles} error={filesError} onReload={loadSourceFiles} />

      <div className="p-4 rounded-md border border-neutral-200 bg-white">
        <label className="flex items-center gap-2 text-sm mb-3">
          <input
            type="checkbox"
            checked={forceMode}
            onChange={(e) => setForceMode(e.target.checked)}
            data-testid="import-force-toggle"
          />
          <span>Force-modus (verwijder bestaande import eerst)</span>
        </label>

        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogTrigger asChild>
            <Button
              disabled={busy}
              data-testid="import-run-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {busy ? "Bezig met importeren…" : "Synchroniseer data nu"}
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="import-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Import bevestigen</DialogTitle>
              <DialogDescription>
                {forceMode
                  ? "Force-modus: bestaande Middenweg/Hoofddorpplein data wordt verwijderd en opnieuw geïmporteerd. Doorgaan?"
                  : "De import wordt gestart. Dit kan tot 30 seconden duren. Doorgaan?"}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="import-cancel-btn">
                Annuleren
              </Button>
              <Button
                onClick={runImport}
                disabled={busy}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="import-confirm-btn"
              >
                {busy ? "Bezig…" : "Ja, starten"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {result && !result.error && (
        <div className="p-4 rounded-md border border-emerald-200 bg-emerald-50 text-sm" data-testid="import-result-ok">
          <div className="font-semibold text-emerald-900 mb-2">Import geslaagd</div>
          <div className="grid grid-cols-2 gap-2 text-emerald-900">
            <div>Kinderen totaal: <strong>{result.kinderen_totaal}</strong></div>
            <div>Ouders totaal: <strong>{result.ouders_totaal}</strong></div>
            <div className="col-span-2">Locaties: <strong>{(result.locaties || []).join(", ")}</strong></div>
          </div>
        </div>
      )}
      {result?.error && (
        <div className="p-4 rounded-md border border-red-200 bg-red-50 text-sm text-red-900" data-testid="import-result-err">
          <div className="font-semibold mb-1">Import mislukt</div>
          <div>{result.error}</div>
        </div>
      )}

      <RepairReferencesBlock />

      <div className="text-xs text-neutral-500">
        Endpoint: <code className="bg-neutral-100 px-1 rounded">POST /api/admin/import-nanny-data</code> · alleen
        beschikbaar voor superadmin. De bronbestanden staan in
        <code className="bg-neutral-100 px-1 rounded ml-1">/app/backend/data/imports/</code>.
      </div>
    </div>
  );
}

/** Losse "Repareer medewerker-referenties" knop — voor als de user al eens
 * geïmporteerd heeft (voordat we de auto-repair toevoegden) en shifts weigeren
 * te saven met "medewerker niet gekoppeld aan deze locatie". */
function RepairReferencesBlock() {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true); setRes(null);
    try {
      const { data } = await api.post("/admin/repair-medewerker-references");
      setRes(data);
      toast.success(`Referenties hersteld · ${data.repaired} medewerker(s), waarvan ${data.fallback_assigned} fallback-toegewezen aan alle locaties`);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Reparatie mislukt";
      toast.error(msg); setRes({ error: msg });
    } finally { setBusy(false); }
  };
  return (
    <div className="p-4 rounded-md border border-blue-200 bg-blue-50" data-testid="repair-refs-block">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="text-sm font-semibold text-blue-900 mb-1">Medewerker-referenties repareren</div>
          <div className="text-xs text-blue-800">
            Klik dit als een medewerker een shift wil krijgen maar de melding
            <em> "niet gekoppeld aan deze locatie"</em> verschijnt. Deze knop koppelt
            elke medewerker met stale referenties aan alle huidige locaties/groepen,
            zodat je in de UI de juiste scope kan kiezen.
          </div>
        </div>
        <Button
          onClick={run}
          disabled={busy}
          data-testid="repair-refs-btn"
          variant="outline"
          className="border-blue-300 text-blue-800 hover:bg-blue-100"
        >
          {busy ? "Bezig…" : "Repareer nu"}
        </Button>
      </div>
      {res && !res.error && (
        <div className="mt-3 text-xs text-blue-900" data-testid="repair-refs-result">
          ✓ {res.repaired} medewerker(s) hersteld · {res.fallback_assigned} fallback-toegewezen ·
          {" "}{res.valid_locations} locaties, {res.valid_groups} groepen in scope.
        </div>
      )}
      {res?.error && (
        <div className="mt-3 text-xs text-red-800">{res.error}</div>
      )}
    </div>
  );
}



/** Drag-and-drop uploader voor de 3 Nanny bronbestanden. Elk slot heeft een
 * vaste bestandsnaam op de server (middenweg.xls, peekaboo.xls, koi.csv). */
const IMPORT_SLOTS = [
  { key: "middenweg", label: "Middenweg (.xls)", accept: ".xls",
    hint: "Webster export van Middenweg locaties — SpreadsheetML XML formaat" },
  { key: "peekaboo", label: "Peekaboo / Hoofddorpplein (.xls)", accept: ".xls",
    hint: "Webster export van Hoofddorpplein locatie" },
  { key: "koi", label: "KOI export (.csv)", accept: ".csv",
    hint: "Kinderopvang Ondernemers Instrument — BSN verrijking (; scheiders)" },
];

function ImportSourceUploader({ files, error, onReload }) {
  return (
    <div className="p-4 rounded-md border border-neutral-200 bg-white" data-testid="import-upload-section">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-neutral-900 mb-1">Bronbestanden uploaden</h3>
        <p className="text-xs text-neutral-600">
          Sleep of kies een bestand voor elk van de 3 slots. De bestanden worden onder een vaste
          naam opgeslagen zodat de import ze direct kan lezen. Bestaande bestanden worden overschreven.
        </p>
      </div>
      {error && (
        <div
          className="mb-3 p-2 rounded border border-red-200 bg-red-50 text-xs text-red-800 flex items-center gap-2"
          data-testid="import-files-error"
        >
          <span>{error}</span>
          <button
            type="button"
            className="ml-auto underline text-red-700 hover:text-red-900"
            onClick={onReload}
            data-testid="import-files-retry"
          >
            Opnieuw laden
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {IMPORT_SLOTS.map((slot) => {
          const info = (files || []).find((f) => f.name === slot.key) || { present: false };
          return (
            <UploadDropzone
              key={slot.key}
              slot={slot}
              info={info}
              onUploaded={onReload}
            />
          );
        })}
      </div>
    </div>
  );
}

function UploadDropzone({ slot, info, onUploaded }) {
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputId = `upload-${slot.key}`;

  const handleFile = async (file) => {
    if (!file) return;
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!slot.accept.split(",").map((e) => e.trim()).includes(ext)) {
      toast.error(`${slot.label} verwacht ${slot.accept} — ontvangen ${ext}`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/import-nanny-data/upload?slot=${slot.key}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${slot.label} geüpload · ${(data.uploaded_bytes / 1024).toFixed(1)} KB`);
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload mislukt");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const statusClass = info.present ? "text-emerald-700" : "text-amber-700";
  const zoneClass = [
    "rounded-md border-2 border-dashed p-4 text-center transition-all cursor-pointer",
    drag ? "border-amber-500 bg-amber-50" : "border-neutral-300 hover:border-neutral-400 bg-neutral-50",
    uploading ? "opacity-60 pointer-events-none" : "",
  ].join(" ");

  return (
    <div className="flex flex-col" data-testid={`upload-slot-${slot.key}`}>
      <label
        htmlFor={inputId}
        className={zoneClass}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
      >
        <div className="text-sm font-medium text-neutral-800">{slot.label}</div>
        <div className="text-[11px] text-neutral-500 mt-1">{slot.hint}</div>
        <div className="text-xs mt-3 text-neutral-600">
          {uploading ? "Uploaden…" : (
            <>Sleep hier <span className="text-neutral-400">of</span>{" "}
              <span className="underline text-amber-700">kies bestand</span></>
          )}
        </div>
        <input
          id={inputId}
          type="file"
          accept={slot.accept}
          className="hidden"
          data-testid={`upload-input-${slot.key}`}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>
      <div className={`mt-2 text-[11px] ${statusClass}`} data-testid={`upload-status-${slot.key}`}>
        {info.present ? (
          <>
            ✓ Aanwezig · {(info.size / 1024).toFixed(1)} KB
            {info.mtime && (
              <span className="text-neutral-400 ml-1">
                · {new Date(info.mtime).toLocaleString("nl-NL")}
              </span>
            )}
          </>
        ) : (
          "⚠ Nog niet geüpload"
        )}
      </div>
    </div>
  );
}


/** Sluitdagen-tab: locatiemanager/superadmin voegen studiedagen, teamuitjes en cursusdagen
 * toe. AI-week en presentie-backfill slaan deze automatisch over (naast NL-feestdagen). */
function SluitdagenTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ datum: "", reden: "", location_id: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, l] = await Promise.all([
        api.get("/sluitdagen"),
        api.get("/locations"),
      ]);
      setRows(r.data.sluitdagen || []);
      setLocations(l.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.datum || !form.reden.trim()) { toast.error("Vul datum + reden in"); return; }
    setBusy(true);
    try {
      await api.post("/sluitdagen", {
        datum: form.datum,
        reden: form.reden.trim(),
        location_id: form.location_id || null,
      });
      toast.success("Sluitdag toegevoegd");
      setForm({ datum: "", reden: "", location_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Toevoegen mislukt"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Sluitdag verwijderen?")) return;
    try {
      await api.delete(`/sluitdagen/${id}`);
      toast.success("Verwijderd");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const seedFeestdagen = async () => {
    const jaar = new Date().getFullYear();
    if (!window.confirm(`Officiële Nederlandse feestdagen voor ${jaar} en ${jaar + 1} als sluitdag toevoegen voor alle locaties in je scope?\n\nBestaande sluitdagen worden nooit overschreven — dit is idempotent.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/sluitdagen/seed-feestdagen", {
        years: [jaar, jaar + 1],
      });
      const nieuw = r.data.inserted || 0;
      const skip = r.data.skipped || 0;
      toast.success(`${nieuw} feestdagen toegevoegd${skip ? ` · ${skip} al aanwezig` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Seed mislukt"); }
    finally { setBusy(false); }
  };

  const locMap = Object.fromEntries(locations.map(l => [l.id, l.name]));
  const isSuperadmin = user?.role === "superadmin";

  return (
    <div className="space-y-4" data-testid="sluitdagen-tab">
      <div>
        <h2 className="font-display text-xl mb-1">Extra sluitdagen</h2>
        <p className="text-sm text-neutral-600">
          Voeg studiedagen, teamuitjes of andere sluitdagen toe. AI-week en presentie-backfill
          slaan deze automatisch over. Wettelijke NL-feestdagen worden altijd al overgeslagen.
        </p>
        <div className="mt-3 p-3 rounded-md border border-emerald-200 bg-emerald-50 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-emerald-900 flex-1 min-w-[240px]">
            🇳🇱 <strong>Automatisch aanvullen</strong>: voeg alle officiële Nederlandse feestdagen
            (Nieuwjaar, Pasen, Koningsdag, Hemelvaart, Pinksteren, Kerst) als sluitdag toe voor het
            huidige + volgende jaar per locatie.
          </span>
          <Button variant="outline" onClick={seedFeestdagen} disabled={busy}
                  data-testid="seed-feestdagen-btn"
                  className="border-emerald-400 text-emerald-800 hover:bg-emerald-100">
            {busy ? "Bezig…" : "🇳🇱 Feestdagen seeden"}
          </Button>
        </div>
      </div>

      <div className="p-4 rounded-md border border-neutral-200 bg-white" data-testid="sluitdag-form">
        <h3 className="text-sm font-semibold text-neutral-900 mb-3">Nieuwe sluitdag</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label>Datum</Label>
            <Input type="date" value={form.datum} onChange={e => setForm({ ...form, datum: e.target.value })} data-testid="sluitdag-datum" />
          </div>
          <div className="md:col-span-2">
            <Label>Reden</Label>
            <Input placeholder="Bijv. Studiedag pedagogisch team"
              value={form.reden} onChange={e => setForm({ ...form, reden: e.target.value })}
              data-testid="sluitdag-reden" />
          </div>
          <div>
            <Label>Locatie</Label>
            <Select value={form.location_id || "org"} onValueChange={v => setForm({ ...form, location_id: v === "org" ? "" : v })}>
              <SelectTrigger data-testid="sluitdag-locatie"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {isSuperadmin && <SelectItem value="org">Alle locaties (organisatie-breed)</SelectItem>}
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={submit} disabled={busy} data-testid="sluitdag-add"
            style={{ background: "var(--sage)" }} className="text-white">
            <Plus className="h-4 w-4 mr-1" /> {busy ? "Bezig…" : "Toevoegen"}
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-neutral-200 bg-white overflow-hidden">
        <div className="px-4 py-2 border-b border-neutral-100 flex items-center gap-2 bg-neutral-50">
          <CalendarX className="h-4 w-4 text-neutral-500" />
          <span className="text-sm font-medium">Bestaande sluitdagen ({rows.length})</span>
        </div>
        {rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-neutral-500">
            Nog geen extra sluitdagen. NL-feestdagen worden automatisch overgeslagen.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-100">
              <tr>
                <th className="text-left py-2 px-4">Datum</th>
                <th className="text-left py-2 px-4">Reden</th>
                <th className="text-left py-2 px-4">Locatie</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-neutral-100" data-testid={`sluitdag-row-${r.id}`}>
                  <td className="py-2 px-4 font-medium text-neutral-800">
                    {new Date(r.datum + "T00:00:00").toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}
                  </td>
                  <td className="py-2 px-4 text-neutral-700">{r.reden}</td>
                  <td className="py-2 px-4 text-xs text-neutral-500">
                    {r.location_id ? (locMap[r.location_id] || "?") : <span className="italic">Alle locaties</span>}
                  </td>
                  <td className="py-2 px-4 text-right">
                    <button
                      type="button"
                      onClick={() => remove(r.id)}
                      className="text-neutral-400 hover:text-red-600 transition-colors"
                      data-testid={`sluitdag-del-${r.id}`}
                      title="Verwijder"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}



/** Sluitingsperiodes-tab: multi-day sluitingen (vakanties, verbouwing).
 * Backend endpoints staan in `sprint1_extensions.py`.
 */
function SluitingsperiodesTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    naam: "", reden: "", start_datum: "", eind_datum: "",
    status: "gepland", location_ids: [], group_ids: [], ouder_bericht: "",
  });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/sluitingsperiodes");
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => {
    setForm({ naam: "", reden: "", start_datum: "", eind_datum: "",
              status: "gepland", location_ids: [], group_ids: [], ouder_bericht: "" });
    setEditingId(null);
  };

  const submit = async () => {
    if (!form.naam.trim() || !form.start_datum || !form.eind_datum) {
      toast.error("Naam, start- en einddatum verplicht"); return;
    }
    if (form.eind_datum < form.start_datum) {
      toast.error("Einddatum mag niet voor startdatum liggen"); return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/sluitingsperiodes/${editingId}`, form);
        toast.success("Sluitingsperiode bijgewerkt");
      } else {
        await api.post("/sluitingsperiodes", form);
        toast.success("Sluitingsperiode toegevoegd");
      }
      reset(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Opslaan mislukt"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Sluitingsperiode verwijderen?")) return;
    try {
      await api.delete(`/sluitingsperiodes/${id}`);
      toast.success("Verwijderd");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const editRow = (r) => {
    setEditingId(r.id);
    setForm({
      naam: r.naam || "", reden: r.reden || "",
      start_datum: r.start_datum || "", eind_datum: r.eind_datum || "",
      status: r.status || "gepland",
      location_ids: r.location_ids || [], group_ids: r.group_ids || [],
      ouder_bericht: r.ouder_bericht || "",
    });
  };

  const STATUS_KLEUR = {
    concept: "bg-neutral-100 text-neutral-700",
    gepland: "bg-blue-100 text-blue-800",
    actief: "bg-emerald-100 text-emerald-800",
    geannuleerd: "bg-red-100 text-red-700",
  };

  return (
    <div className="space-y-6" data-testid="sluitingsperiodes-tab">
      <Card className="p-4 bg-white">
        <div className="text-sm font-semibold text-neutral-800 mb-3">
          {editingId ? "Sluitingsperiode wijzigen" : "Nieuwe sluitingsperiode"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Naam *</Label>
            <Input value={form.naam} onChange={e => setForm({ ...form, naam: e.target.value })}
                   placeholder="bijv. Zomervakantie 2026" data-testid="periode-naam" />
          </div>
          <div>
            <Label className="text-xs">Startdatum *</Label>
            <Input type="date" value={form.start_datum}
                   onChange={e => setForm({ ...form, start_datum: e.target.value })}
                   data-testid="periode-start" />
          </div>
          <div>
            <Label className="text-xs">Einddatum *</Label>
            <Input type="date" value={form.eind_datum}
                   onChange={e => setForm({ ...form, eind_datum: e.target.value })}
                   data-testid="periode-eind" />
          </div>
          <div>
            <Label className="text-xs">Reden</Label>
            <Input value={form.reden} onChange={e => setForm({ ...form, reden: e.target.value })}
                   placeholder="Jaarlijkse sluiting, verbouwing, ..." data-testid="periode-reden" />
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="periode-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="concept">Concept</SelectItem>
                <SelectItem value="gepland">Gepland</SelectItem>
                <SelectItem value="actief">Actief</SelectItem>
                <SelectItem value="geannuleerd">Geannuleerd</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Bericht voor ouders (optioneel)</Label>
            <Textarea rows={2} value={form.ouder_bericht}
                      onChange={e => setForm({ ...form, ouder_bericht: e.target.value })}
                      placeholder="Wordt getoond in het ouderportaal" data-testid="periode-ouder" />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          {editingId && (
            <Button variant="outline" onClick={reset}>Annuleren</Button>
          )}
          <Button onClick={submit} disabled={busy} className="text-white" style={{ background: "var(--sage)" }}
                  data-testid="periode-save">
            {busy ? "Opslaan…" : editingId ? "Bijwerken" : "Toevoegen"}
          </Button>
        </div>
      </Card>

      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        <div className="p-3 border-b border-neutral-200 text-xs font-semibold text-neutral-600 uppercase tracking-wide">
          Bestaande sluitingsperiodes ({rows.length})
        </div>
        {rows.length === 0 && (
          <div className="p-6 text-center text-sm text-neutral-500">
            Nog geen sluitingsperiodes.
          </div>
        )}
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50">
              <tr className="text-xs text-neutral-500 uppercase tracking-wide">
                <th className="text-left py-2 px-4">Periode</th>
                <th className="text-left py-2 px-4">Naam / Reden</th>
                <th className="text-left py-2 px-4">Status</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-neutral-100" data-testid={`periode-row-${r.id}`}>
                  <td className="py-2 px-4 font-mono text-xs text-neutral-700">
                    {r.start_datum} → {r.eind_datum}
                  </td>
                  <td className="py-2 px-4">
                    <div className="font-medium text-neutral-800">{r.naam}</div>
                    {r.reden && <div className="text-xs text-neutral-500">{r.reden}</div>}
                  </td>
                  <td className="py-2 px-4">
                    <Badge className={`${STATUS_KLEUR[r.status] || ""} text-[10px]`}>{r.status}</Badge>
                  </td>
                  <td className="py-2 px-4 text-right">
                    <button onClick={() => editRow(r)} className="text-neutral-400 hover:text-blue-600 mr-2"
                            title="Wijzig" data-testid={`periode-edit-${r.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(r.id)} className="text-neutral-400 hover:text-red-600"
                            title="Verwijder" data-testid={`periode-del-${r.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


/** Demo-data opschonen: scan+delete van seed-records binnen deze tenant.
 * Alleen directeur/superadmin/platform_admin. Vereist typed "VERWIJDER DEMO DATA"
 * bevestiging voordat de destructieve delete wordt gestuurd. */
const CLEANUP_COLL_LABELS = {
  medewerkers: "Medewerkers",
  children: "Kinderen",
  parents: "Ouders",
  contracts: "Contracten",
  invoices: "Facturen",
  aanmeldingen: "Aanmeldingen",
  wachtlijst: "Wachtlijst",
  rooster: "Rooster-shifts",
  presentie: "Presentie",
  personeelsdiensten: "Personeelsdiensten",
  schriftje: "Schriftjes",
  nieuwsbrief: "Nieuwsbrieven",
  mentorbericht: "Mentorberichten",
  daglog: "Daglog-events",
  sluitdagen: "Sluitdagen (auto)",
  feestdagen: "Feestdagen (auto)",
};

function CleanupTab() {
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmText, setConfirmText] = useState("");
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  // ── Tenant-integriteit (weesrecords) ─────────────────────────────────
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [orphanSel, setOrphanSel] = useState(new Set());
  const [orphanConfirm, setOrphanConfirm] = useState("");
  const [orphanFixing, setOrphanFixing] = useState(false);
  const [orphanResult, setOrphanResult] = useState(null);

  const runDiagnose = async () => {
    setDiagLoading(true);
    setOrphanResult(null);
    try {
      const { data } = await api.get("/admin/cleanup/diagnose");
      setDiag(data);
      // Standaard alle weesrecord-groepen aanvinken
      setOrphanSel(new Set(Object.keys(data.orphans || {})));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Diagnose mislukt");
    } finally {
      setDiagLoading(false);
    }
  };

  const toggleOrphan = (key) => {
    const next = new Set(orphanSel);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setOrphanSel(next);
  };

  const applyOrphanFix = async () => {
    if (orphanConfirm.trim() !== "REPAREER WEESRECORDS") {
      toast.error('Typ exact "REPAREER WEESRECORDS" ter bevestiging'); return;
    }
    if (orphanSel.size === 0) { toast.error("Geen categorie geselecteerd"); return; }
    setOrphanFixing(true);
    try {
      const { data } = await api.post("/admin/cleanup/fix-orphans", {
        orphan_keys: Array.from(orphanSel),
        confirm_text: "REPAREER WEESRECORDS",
      });
      setOrphanResult(data);
      toast.success(`Opgeruimd · ${data.deleted_total} weesrecords verwijderd`);
      setDiag(null); setOrphanConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reparatie mislukt");
    } finally { setOrphanFixing(false); }
  };

  const runScan = async () => {
    setScanning(true);
    setResult(null);
    try {
      const { data } = await api.get("/admin/cleanup/scan");
      setScan(data);
      // Standaard alle collecties aanvinken die matches hebben
      const preselect = new Set(
        Object.entries(data.per_collection)
          .filter(([, v]) => (v?.count || 0) > 0)
          .map(([k]) => k)
      );
      setSelected(preselect);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scan mislukt");
    } finally {
      setScanning(false);
    }
  };

  const toggleColl = (coll) => {
    const next = new Set(selected);
    if (next.has(coll)) next.delete(coll);
    else next.add(coll);
    setSelected(next);
  };

  const apply = async () => {
    if (confirmText.trim() !== "VERWIJDER DEMO DATA") {
      toast.error('Typ exact "VERWIJDER DEMO DATA" ter bevestiging');
      return;
    }
    if (selected.size === 0) {
      toast.error("Geen collecties geselecteerd");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/admin/cleanup/apply", {
        collections: Array.from(selected),
        confirm_text: "VERWIJDER DEMO DATA",
      });
      setResult(data);
      toast.success(`Opgeschoond · ${data.deleted_total} records verwijderd`);
      setScan(null);
      setConfirmText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Opschoning mislukt");
    } finally {
      setApplying(false);
    }
  };

  const totalMatches = scan
    ? Object.entries(scan.per_collection).reduce((sum, [, v]) => sum + (v?.count || 0), 0)
    : 0;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="cleanup-tab">
      <div>
        <h2 className="font-display text-xl mb-1">Demo-data opschonen</h2>
        <p className="text-sm text-neutral-600">
          Verwijdert medewerkers, kinderen, contracten, facturen en gekoppelde records
          in <strong>jouw tenant</strong> die herkend worden als demo of seed. Detectie op
          basis van {" "}
          <code className="bg-neutral-100 px-1 rounded text-[11px]">demo_tag</code>,
          seed-ID-formaten (<code className="bg-neutral-100 px-1 rounded text-[11px]">mw-XX</code>,
          <code className="bg-neutral-100 px-1 rounded text-[11px] ml-1">kind-XX</code>),
          demo-e-mails (<code className="bg-neutral-100 px-1 rounded text-[11px]">@example.nl</code>,
          <code className="bg-neutral-100 px-1 rounded text-[11px] ml-1">@dezonnebloem.nl</code>,
          <code className="bg-neutral-100 px-1 rounded text-[11px] ml-1">regenboog-demo.example</code>),
          seed-BSN-prefixes en notities zoals "Seed - extra pedagogisch".
        </p>
      </div>

      {/* ── Sectie 1: Tenant-integriteit (weesrecords) ─────────────────── */}
      <div className="p-4 rounded-md border border-amber-200 bg-amber-50" data-testid="cleanup-diagnose-block">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="text-sm font-semibold text-amber-900 mb-1">
              Tenant-integriteit controleren
            </div>
            <p className="text-xs text-amber-900">
              Zoekt <strong>weesrecords</strong>: groepen zonder bestaande locatie, kinderen zonder
              bestaande groep, rooster-shifts zonder bestaande medewerker/groep, contracten en
              facturen naar verwijderde kinderen. Dit is waarschijnlijk de oorzaak als je bij
              <em> locatie</em> nog kindernamen ziet of bij <em>groepen</em> niks terugziet.
            </p>
          </div>
          <Button onClick={runDiagnose} disabled={diagLoading}
                  variant="outline"
                  className="border-amber-400 text-amber-900 hover:bg-amber-100"
                  data-testid="cleanup-diagnose-btn">
            {diagLoading ? "Bezig…" : (diag ? "Opnieuw diagnoseren" : "Diagnose uitvoeren")}
          </Button>
        </div>

        {diag && (
          <div className="mt-4 space-y-3" data-testid="cleanup-diagnose-panel">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {Object.entries(diag.counts).map(([k, v]) => (
                <div key={k} className="bg-white rounded p-2 border border-amber-200">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">{CLEANUP_COLL_LABELS[k] || k}</div>
                  <div className="font-semibold text-neutral-900">{v}</div>
                </div>
              ))}
            </div>

            {diag.total_orphans === 0 ? (
              <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="cleanup-no-orphans">
                ✓ Geen weesrecords gevonden — tenant-integriteit is in orde
              </div>
            ) : (
              <>
                <div className="text-xs font-semibold text-amber-900">
                  {diag.total_orphans} weesrecord(s) gevonden — selecteer wat opgeruimd moet worden:
                </div>
                <div className="rounded border border-amber-200 bg-white overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-amber-100 text-[11px] uppercase tracking-widest text-neutral-500">
                        <th className="text-left py-2 px-3 w-12"></th>
                        <th className="text-left py-2 px-3">Categorie</th>
                        <th className="text-right py-2 px-3 w-20">Aantal</th>
                        <th className="text-left py-2 px-3">Voorbeelden</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(diag.orphans).map(([key, b]) => (
                        <tr key={key} className="border-b border-amber-100 last:border-0" data-testid={`orphan-row-${key}`}>
                          <td className="py-2 px-3">
                            <input type="checkbox" checked={orphanSel.has(key)}
                                   onChange={() => toggleOrphan(key)}
                                   data-testid={`orphan-check-${key}`} />
                          </td>
                          <td className="py-2 px-3">
                            <div className="font-medium">{CLEANUP_COLL_LABELS[b.collection] || b.collection}</div>
                            <div className="text-[11px] text-neutral-500">{b.reason}</div>
                          </td>
                          <td className="py-2 px-3 text-right">
                            <Badge variant="outline" className="bg-amber-100 text-amber-900 border-amber-300">{b.count}</Badge>
                          </td>
                          <td className="py-2 px-3 text-xs text-neutral-600">
                            <ul className="space-y-0.5">
                              {(b.sample || []).slice(0, 3).map((s) => (
                                <li key={s.id}>{s.label || s.id}</li>
                              ))}
                              {b.count > 3 && <li className="text-neutral-400 italic">… en {b.count - 3} meer</li>}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="p-3 rounded-md border border-red-300 bg-red-50 space-y-2" data-testid="orphan-confirm-block">
                  <div className="text-xs text-red-800">
                    Type <code className="bg-white px-1.5 py-0.5 border border-red-200 rounded font-mono">REPAREER WEESRECORDS</code>{" "}
                    om de {orphanSel.size} geselecteerde categorie(ën) te wissen.
                  </div>
                  <Input value={orphanConfirm} onChange={(e) => setOrphanConfirm(e.target.value)}
                         placeholder="REPAREER WEESRECORDS" className="max-w-xs bg-white"
                         data-testid="orphan-confirm-input" />
                  <Button onClick={applyOrphanFix}
                          disabled={orphanFixing || orphanConfirm.trim() !== "REPAREER WEESRECORDS" || orphanSel.size === 0}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid="orphan-apply-btn">
                    {orphanFixing ? "Verwijderen…" : `Weesrecords verwijderen (${orphanSel.size})`}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {orphanResult && (
          <div className="mt-3 p-3 rounded-md border border-emerald-200 bg-emerald-50 text-sm" data-testid="orphan-result">
            ✓ {orphanResult.deleted_total} weesrecord(s) verwijderd
          </div>
        )}
      </div>

      {/* ── Sectie 2: Demo/seed-data ────────────────────────────────────── */}
      <div className="p-4 rounded-md border border-red-200 bg-red-50 text-sm text-red-900">
        <div className="font-semibold mb-1">Let op — permanente verwijdering</div>
        <ul className="list-disc ml-5 space-y-0.5 text-[13px]">
          <li>Records worden onherstelbaar verwijderd (geen prullenbak).</li>
          <li>Alleen records met een expliciete demo/seed-markering worden verwijderd. Records die je zélf hebt aangemaakt blijven behouden.</li>
          <li>Alle acties worden in het audit-log vastgelegd.</li>
          <li>Draai eerst de scan en controleer de lijst voordat je bevestigt.</li>
        </ul>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={runScan} disabled={scanning} data-testid="cleanup-scan-btn"
                style={{ background: "var(--sage)" }} className="text-white">
          {scanning ? "Scannen…" : (scan ? "Opnieuw scannen" : "Scan uitvoeren")}
        </Button>
        {scan && (
          <span className="text-sm text-neutral-600" data-testid="cleanup-scan-total">
            {totalMatches === 0
              ? "Geen demo-data gevonden — je tenant is schoon"
              : `${totalMatches} record(s) herkend als demo/seed`}
          </span>
        )}
      </div>

      {scan && totalMatches > 0 && (
        <div className="rounded-md border border-neutral-200 bg-white overflow-hidden" data-testid="cleanup-scan-panel">
          <div className="px-4 py-2 border-b border-neutral-100 bg-neutral-50 text-xs uppercase tracking-widest text-neutral-600 font-semibold">
            Scan-resultaat per collectie
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-100 text-[11px] uppercase tracking-widest text-neutral-500">
                <th className="text-left py-2 px-3 w-12"></th>
                <th className="text-left py-2 px-3">Collectie</th>
                <th className="text-right py-2 px-3 w-24">Aantal</th>
                <th className="text-left py-2 px-3">Voorbeelden (max 5)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(scan.per_collection).map(([coll, v]) => {
                const count = v?.count || 0;
                if (count === 0) return null;
                const isSel = selected.has(coll);
                const samples = (v?.sample || []).slice(0, 5);
                return (
                  <tr key={coll} className="border-b border-neutral-100 last:border-0" data-testid={`cleanup-row-${coll}`}>
                    <td className="py-2 px-3">
                      <input type="checkbox" checked={isSel} onChange={() => toggleColl(coll)}
                             data-testid={`cleanup-check-${coll}`} />
                    </td>
                    <td className="py-2 px-3 font-medium">{CLEANUP_COLL_LABELS[coll] || coll}</td>
                    <td className="py-2 px-3 text-right">
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{count}</Badge>
                    </td>
                    <td className="py-2 px-3 text-xs text-neutral-600">
                      {samples.length === 0 ? (
                        <span className="italic text-neutral-400">Cascade — via andere records</span>
                      ) : (
                        <ul className="space-y-0.5">
                          {samples.map((s) => (
                            <li key={s.id}>
                              <span>{s.label || s.id}</span>
                              <span className="text-neutral-400 ml-2">[{s.reden}]</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {scan && totalMatches > 0 && (
        <div className="p-4 rounded-md border border-red-300 bg-red-50 space-y-3" data-testid="cleanup-confirm-block">
          <div className="text-sm font-semibold text-red-900">Bevestiging vereist</div>
          <div className="text-xs text-red-800">
            Type <code className="bg-white px-1.5 py-0.5 border border-red-200 rounded font-mono">VERWIJDER DEMO DATA</code>{" "}
            in het veld hieronder om de {selected.size} geselecteerde collectie(s) op te schonen.
          </div>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="VERWIJDER DEMO DATA"
            className="max-w-xs bg-white"
            data-testid="cleanup-confirm-input"
          />
          <div>
            <Button
              onClick={apply}
              disabled={applying || confirmText.trim() !== "VERWIJDER DEMO DATA" || selected.size === 0}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="cleanup-apply-btn"
            >
              {applying ? "Verwijderen…" : `Definitief verwijderen (${selected.size} categorie${selected.size === 1 ? "" : "ën"})`}
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="p-4 rounded-md border border-emerald-200 bg-emerald-50 text-sm" data-testid="cleanup-result">
          <div className="font-semibold text-emerald-900 mb-2">
            ✓ Opschoning voltooid — {result.deleted_total} records verwijderd
          </div>
          <ul className="list-disc ml-5 text-emerald-900 text-xs">
            {Object.entries(result.deleted_per_collection).filter(([, n]) => n > 0).map(([coll, n]) => (
              <li key={coll}>{CLEANUP_COLL_LABELS[coll] || coll}: {n}</li>
            ))}
          </ul>
          {result.snapshot_id && (
            <div className="mt-2 text-[11px] text-emerald-900">
              Snapshot bewaard: <code className="bg-white px-1 rounded">{result.snapshot_id.slice(0, 8)}…</code> — 7 dagen herstelbaar via "Herstel laatste opschoning" onderaan.
            </div>
          )}
        </div>
      )}

      <UndoSnapshotsBlock />
      <ContractLocationSyncBlock />
      <SuspiciousLocationsBlock />
      <NoTenantBlock />
      )}
    </div>
  );
}


/** Undo-blok: toont recente snapshots + herstelt op verzoek. */
function UndoSnapshotsBlock() {
  const [snaps, setSnaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cleanup/snapshots");
      setSnaps(data.snapshots || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Snapshots ophalen mislukt");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const undo = async (snapshotId) => {
    if (restoreConfirm.trim() !== "HERSTEL VERWIJDERING") {
      toast.error('Typ exact "HERSTEL VERWIJDERING" ter bevestiging'); return;
    }
    setRestoring(snapshotId);
    try {
      const { data } = await api.post("/admin/cleanup/undo", {
        snapshot_id: snapshotId,
        confirm_text: "HERSTEL VERWIJDERING",
      });
      toast.success(`Hersteld · ${data.restored_total} records teruggeplaatst`);
      setRestoreConfirm("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Herstel mislukt");
    } finally { setRestoring(null); }
  };

  return (
    <div className="p-4 rounded-md border border-blue-200 bg-blue-50" data-testid="undo-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-blue-900">Herstel laatste opschoning</div>
          <div className="text-xs text-blue-800">
            Snapshots blijven <strong>7 dagen</strong> beschikbaar. Handig als je per ongeluk te veel wist.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}
                className="border-blue-400 text-blue-900 hover:bg-blue-100"
                data-testid="undo-refresh-btn">
          {loading ? "Bezig…" : "Herlaad"}
        </Button>
      </div>
      {snaps.length === 0 ? (
        <div className="mt-3 text-xs text-blue-900 italic">Geen recente snapshots — er is niks te herstellen.</div>
      ) : (
        <div className="mt-3 space-y-2">
          <Input value={restoreConfirm} onChange={(e) => setRestoreConfirm(e.target.value)}
                 placeholder='Typ "HERSTEL VERWIJDERING" om te bevestigen'
                 className="max-w-md bg-white text-xs"
                 data-testid="undo-confirm-input" />
          <div className="rounded border border-blue-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 text-[11px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left py-2 px-3">Datum</th>
                  <th className="text-left py-2 px-3">Type</th>
                  <th className="text-left py-2 px-3">Door</th>
                  <th className="text-right py-2 px-3">Records</th>
                  <th className="text-left py-2 px-3">Details</th>
                  <th className="w-32 text-right py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {snaps.map(s => (
                  <tr key={s.id} className="border-b border-blue-100 last:border-0" data-testid={`snap-row-${s.id}`}>
                    <td className="py-2 px-3 text-xs text-neutral-700">
                      {s.created_at ? new Date(s.created_at).toLocaleString("nl-NL") : "?"}
                    </td>
                    <td className="py-2 px-3">
                      <Badge variant="outline" className="text-[10px]">
                        {s.kind === "orphan_fix" ? "Weesrecords" : "Demo-cleanup"}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-xs text-neutral-600">{s.actor_email || "—"}</td>
                    <td className="py-2 px-3 text-right font-medium">{s.total}</td>
                    <td className="py-2 px-3 text-[11px] text-neutral-600">
                      {Object.entries(s.counts || {}).map(([k, n]) => `${CLEANUP_COLL_LABELS[k] || k}: ${n}`).join(" · ")}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <Button size="sm" variant="outline"
                              onClick={() => undo(s.id)}
                              disabled={restoring === s.id || restoreConfirm.trim() !== "HERSTEL VERWIJDERING"}
                              data-testid={`snap-undo-${s.id}`}
                              className="border-blue-400 text-blue-900 hover:bg-blue-50">
                        {restoring === s.id ? "Herstellen…" : "Herstel"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


/** Records zonder tenant — alleen platform-superadmin. Toont bovenste 10
 * records per collectie waar `organization_id` leeg/ontbreekt (weeskinderen
 * buiten alle tenants). */
function NoTenantBlock() {
  const { user } = useAuth();
  const isPlatform = user?.role === "platform_admin" || (user?.role === "superadmin" && !user?.organization_id);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cleanup/no-tenant");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scan mislukt");
    } finally { setLoading(false); }
  };

  if (!isPlatform) return null;

  return (
    <div className="p-4 rounded-md border border-purple-200 bg-purple-50" data-testid="no-tenant-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-purple-900">Records zonder tenant (platform-super)</div>
          <div className="text-xs text-purple-800">
            Scant records met <code className="bg-white px-1 rounded">organization_id=null</code> —
            weesrecords buiten alle KDV's, meestal restanten van oude imports of legacy seeds.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={run} disabled={loading}
                className="border-purple-400 text-purple-900 hover:bg-purple-100"
                data-testid="no-tenant-run-btn">
          {loading ? "Scannen…" : (data ? "Opnieuw scannen" : "Scan")}
        </Button>
      </div>
      {data && (
        <div className="mt-3">
          <div className="text-xs text-purple-900 mb-2">
            <strong>{data.total_no_tenant}</strong> record(s) zonder organization_id gevonden
          </div>
          {data.total_no_tenant > 0 && (
            <div className="rounded border border-purple-200 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-purple-100 text-[11px] uppercase tracking-widest text-neutral-500">
                    <th className="text-left py-2 px-3">Collectie</th>
                    <th className="text-right py-2 px-3 w-24">Aantal</th>
                    <th className="text-left py-2 px-3">Bovenste 10 samples</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.per_collection).map(([coll, v]) => (
                    <tr key={coll} className="border-b border-purple-100 last:border-0" data-testid={`no-tenant-row-${coll}`}>
                      <td className="py-2 px-3 font-medium">{CLEANUP_COLL_LABELS[coll] || coll}</td>
                      <td className="py-2 px-3 text-right">
                        <Badge variant="outline" className="bg-purple-100 text-purple-900 border-purple-300">{v.count}</Badge>
                      </td>
                      <td className="py-2 px-3 text-xs text-neutral-600">
                        <ul className="space-y-0.5">
                          {(v.sample || []).map((s) => (
                            <li key={s.id}>{s.label || s.id}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-2 text-[11px] text-purple-900 italic">
            Records zonder tenant kunnen niet via de reguliere Nanny-Poppins cleanup verwijderd worden — gebruik het CLI-script
            <code className="bg-white px-1 rounded ml-1">python -m scripts.purge_demo_tenant</code> voor bulk-cleanup, of neem contact op met development.
          </div>
        </div>
      )}
    </div>
  );
}


/** SuspiciousLocationsBlock — detecteert locaties die eigenlijk kindnamen zijn
 * (foutieve CSV-import). Toont per locatie de reden + gekoppelde records,
 * met checkbox-selectie en typed bevestiging "VERWIJDER LOCATIES". */
function SuspiciousLocationsBlock() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/admin/cleanup/suspicious-locations");
      setData(data);
      // Preselect ALLEEN de veilig-te-verwijderen locaties
      setSelected(new Set((data.suspicious || []).filter(s => s.safe_to_delete).map(s => s.id)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scan mislukt");
    } finally { setLoading(false); }
  };

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setSelected(n);
  };

  const apply = async () => {
    if (confirmText.trim() !== "VERWIJDER LOCATIES") {
      toast.error('Typ exact "VERWIJDER LOCATIES" ter bevestiging'); return;
    }
    if (selected.size === 0) { toast.error("Geen locaties geselecteerd"); return; }
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/cleanup/delete-locations", {
        location_ids: Array.from(selected),
        confirm_text: "VERWIJDER LOCATIES",
      });
      setResult(data);
      toast.success(`${data.deleted} locatie(s) verwijderd`);
      setData(null); setConfirmText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verwijderen mislukt");
    } finally { setDeleting(false); }
  };

  return (
    <div className="p-4 rounded-md border border-orange-300 bg-orange-50" data-testid="susploc-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="text-sm font-semibold text-orange-900 mb-1">
            Verdachte locaties opsporen
          </div>
          <p className="text-xs text-orange-900">
            Detecteert locaties in de <em>Kies locatie</em>-dropdown die er niet uitzien als echte KDV's:
            naam heeft komma-formaat ("Achternaam, Voornaam"), matcht met een kind/ouder in de tenant,
            of heeft geen groepen/medewerkers gekoppeld. Meestal veroorzaakt door een verkeerde CSV-import.
          </p>
        </div>
        <Button onClick={run} disabled={loading}
                variant="outline"
                className="border-orange-500 text-orange-900 hover:bg-orange-100"
                data-testid="susploc-scan-btn">
          {loading ? "Scannen…" : (data ? "Opnieuw scannen" : "Scan locaties")}
        </Button>
      </div>

      {data && (
        <div className="mt-3 space-y-3">
          {data.total === 0 ? (
            <div className="text-xs text-emerald-900 bg-emerald-50 border border-emerald-200 rounded p-2">
              ✓ Geen verdachte locaties gevonden
            </div>
          ) : (
            <>
              <div className="text-xs text-orange-900">
                <strong>{data.total}</strong> verdachte locatie(s) gevonden. Vinkjes staan aan voor locaties die veilig te verwijderen zijn (geen refs).
              </div>
              <div className="rounded border border-orange-200 bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-orange-100 text-[11px] uppercase tracking-widest text-neutral-500">
                      <th className="text-left py-2 px-3 w-10"></th>
                      <th className="text-left py-2 px-3">Naam</th>
                      <th className="text-left py-2 px-3">Redenen</th>
                      <th className="text-left py-2 px-3">Refs</th>
                      <th className="text-left py-2 px-3 w-24">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.suspicious.map(s => (
                      <tr key={s.id} className="border-b border-orange-100 last:border-0" data-testid={`susploc-row-${s.id}`}>
                        <td className="py-2 px-3">
                          <input type="checkbox" checked={selected.has(s.id)}
                                 onChange={() => toggle(s.id)}
                                 disabled={!s.safe_to_delete}
                                 data-testid={`susploc-check-${s.id}`} />
                        </td>
                        <td className="py-2 px-3 font-medium">{s.name}</td>
                        <td className="py-2 px-3 text-[11px] text-neutral-600">
                          <ul className="space-y-0.5">
                            {s.reasons.map((r) => <li key={r}>• {r}</li>)}
                          </ul>
                        </td>
                        <td className="py-2 px-3 text-[11px] text-neutral-600">
                          {Object.entries(s.refs).filter(([, n]) => n > 0)
                            .map(([k, n]) => `${k}=${n}`).join(" · ") || "—"}
                        </td>
                        <td className="py-2 px-3">
                          {s.safe_to_delete ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">Veilig</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200 text-[10px]">Heeft refs</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="p-3 rounded-md border border-red-300 bg-red-50 space-y-2" data-testid="susploc-confirm-block">
                <div className="text-xs text-red-800">
                  Type <code className="bg-white px-1.5 py-0.5 border border-red-200 rounded font-mono">VERWIJDER LOCATIES</code>{" "}
                  om de {selected.size} geselecteerde locatie(s) te wissen.
                </div>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                       placeholder="VERWIJDER LOCATIES" className="max-w-xs bg-white"
                       data-testid="susploc-confirm-input" />
                <Button onClick={apply}
                        disabled={deleting || confirmText.trim() !== "VERWIJDER LOCATIES" || selected.size === 0}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="susploc-apply-btn">
                  {deleting ? "Verwijderen…" : `Verwijder ${selected.size} locatie(s)`}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {result && (
        <div className="mt-3 p-3 rounded-md border border-emerald-200 bg-emerald-50 text-sm" data-testid="susploc-result">
          ✓ {result.deleted} locatie(s) verwijderd
          {result.blocked?.length > 0 && (
            <div className="text-xs mt-1 text-red-800">
              {result.blocked.length} locatie(s) geblokkeerd (nog gekoppeld aan records) — bekijk in de gewone Locaties-tab.
            </div>
          )}
          {result.snapshot_id && (
            <div className="text-[11px] mt-1">
              Snapshot: <code className="bg-white px-1 rounded">{result.snapshot_id.slice(0, 8)}…</code> — 7 dagen herstelbaar.
            </div>
          )}
        </div>
      )}
    </div>
  );
}



/** ContractLocationSyncBlock — kopieert `location_id` en `group_id` van
 * kind naar contract voor alle mismatches in deze tenant. */
function ContractLocationSyncBlock() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  const scan = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/admin/cleanup/contract-location-mismatch");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scan mislukt");
    } finally { setLoading(false); }
  };

  const apply = async () => {
    if (confirmText.trim() !== "SYNC LOCATIES") {
      toast.error('Typ exact "SYNC LOCATIES" ter bevestiging'); return;
    }
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/cleanup/sync-contract-locations", {
        confirm_text: "SYNC LOCATIES",
      });
      setResult(data);
      toast.success(`${data.updated} contract(en) gesynchroniseerd`);
      setData(null); setConfirmText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sync mislukt");
    } finally { setSyncing(false); }
  };

  return (
    <div className="p-4 rounded-md border border-teal-300 bg-teal-50" data-testid="contract-sync-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="text-sm font-semibold text-teal-900 mb-1">
            Contracten synchroniseren met kind-locatie
          </div>
          <p className="text-xs text-teal-900">
            Kopieert de <strong>locatie</strong> en <strong>groep</strong> van elk kind
            naar het gekoppelde contract. Voorkomt dat kinderen op locatie A staan
            terwijl hun contract op locatie B ligt (mismatch bij rooster/facturatie).
            Nieuwe contracten erven dit voortaan automatisch over.
          </p>
        </div>
        <Button onClick={scan} disabled={loading}
                variant="outline"
                className="border-teal-500 text-teal-900 hover:bg-teal-100"
                data-testid="contract-sync-scan-btn">
          {loading ? "Scannen…" : (data ? "Opnieuw scannen" : "Scan mismatches")}
        </Button>
      </div>

      {data && (
        <div className="mt-3 space-y-3">
          {data.total === 0 ? (
            <div className="text-xs text-emerald-900 bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="contract-sync-clean">
              ✓ Alle contracten staan op de juiste locatie/groep
            </div>
          ) : (
            <>
              <div className="text-xs text-teal-900">
                <strong>{data.total}</strong> contract(en) op andere locatie/groep dan het kind
              </div>
              <div className="rounded border border-teal-200 bg-white overflow-hidden max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-teal-50">
                    <tr className="border-b border-teal-100 text-[11px] uppercase tracking-widest text-neutral-500">
                      <th className="text-left py-2 px-3">Kind</th>
                      <th className="text-left py-2 px-3">Huidige loc → nieuwe loc</th>
                      <th className="text-left py-2 px-3">Groep</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.mismatches.slice(0, 100).map(m => (
                      <tr key={m.contract_id} className="border-b border-teal-100 last:border-0" data-testid={`sync-row-${m.contract_id}`}>
                        <td className="py-2 px-3 font-medium">{m.kind_naam || m.kind_id}</td>
                        <td className="py-2 px-3 text-xs text-neutral-700">
                          <code className="bg-neutral-100 px-1 rounded">{m.current_location_id || "—"}</code>{" → "}
                          <code className="bg-teal-100 px-1 rounded">{m.target_location_id || "—"}</code>
                        </td>
                        <td className="py-2 px-3 text-xs text-neutral-700">
                          {m.will_update_group ? (
                            <>
                              <code className="bg-neutral-100 px-1 rounded">{m.current_group_id || "—"}</code>{" → "}
                              <code className="bg-teal-100 px-1 rounded">{m.target_group_id || "—"}</code>
                            </>
                          ) : <span className="text-neutral-400">idem</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.total > 100 && <div className="text-[11px] text-neutral-500">Toont eerste 100 — alle {data.total} worden gesynchroniseerd bij klik.</div>}
              <div className="p-3 rounded-md border border-teal-400 bg-white space-y-2">
                <div className="text-xs text-teal-900">
                  Typ <code className="bg-neutral-100 px-1.5 py-0.5 rounded font-mono">SYNC LOCATIES</code> om alle mismatches recht te trekken.
                </div>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                       placeholder="SYNC LOCATIES" className="max-w-xs"
                       data-testid="contract-sync-confirm-input" />
                <Button onClick={apply}
                        disabled={syncing || confirmText.trim() !== "SYNC LOCATIES"}
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                        data-testid="contract-sync-apply-btn">
                  {syncing ? "Synchroniseren…" : `Sync ${data.total} contract(en)`}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {result && (
        <div className="mt-3 p-3 rounded-md border border-emerald-200 bg-emerald-50 text-sm" data-testid="contract-sync-result">
          ✓ {result.updated} contract(en) gesynchroniseerd
          {result.snapshot_id && (
            <div className="text-[11px] mt-1">
              Snapshot: <code className="bg-white px-1 rounded">{result.snapshot_id.slice(0, 8)}…</code> — 7 dagen herstelbaar via undo.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

